import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "./Product.module.css";
import Spin from "../Spinner/Spinner";
import { API } from "../../config";
import { FaPlus, FaUpload, FaDownload, FaArrowsAlt } from "react-icons/fa";
import axios from "axios";
import Toggle from "../../utils/Toggle/Toggle";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function Product() {
  const [showingActiveProducts, setShowingActiveProducts] = useState(true);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [spin, setSpin] = useState(true);
  const [updatingAvailability, setUpdatingAvailability] = useState(false);
  const [pendingAvailability, setPendingAvailability] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState("");
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [originalOrders, setOriginalOrders] = useState({});
  const [changedGroups, setChangedGroups] = useState({});
  const [showMoveCategoryDialog, setShowMoveCategoryDialog] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const bulkUpdate = async (available) => {
    try {
      await axios.put(
        `${API}/Admin/addProduct/bulk/${available ? "active" : "in-active"}`,
        {
          productIds: selectedProductIds,
        }
      );
      getData();
      setSelectedProductIds([]);
    } catch (error) {
      console.log(error);
    }
  };

  const updateProductAvailability = async (productId, available) => {
    setUpdatingAvailability(true);
    try {
      await axios.put(
        `${API}/Admin/addProduct/bulk/${available ? "active" : "in-active"}`,
        {
          productIds: [productId],
        }
      );
      await getData();
    } catch (error) {
      console.log(error);
    } finally {
      setUpdatingAvailability(false);
    }
  };

  const requestAvailabilityChange = (product, available) => {
    setPendingAvailability({ product, available });
  };

  const confirmAvailabilityChange = async () => {
    const { product, available } = pendingAvailability;
    setPendingAvailability(null);
    await updateProductAvailability(product._id, available);
  };

  const updateProductPrice = async (productId, price) => {
    try {
      await axios.put(`${API}/Admin/addProduct/${productId}/price`, {
        price,
      });
      getData();
      setSelectedProductIds([]);
    } catch (error) {
      console.log(error);
    }
  };

  const getCategories = async () => {
    const data = await fetch(`${API}/Admin/category/get`);
    const json = await data.json();
    setCategories(json.allcategory);
    getData();
    json.allcategory.map((item) => {
      if (!!item.subCategories && item.subCategories.length > 0) {
        setSubcategories((prev) => {
          const existingIds = new Set(prev.map((sub) => sub._id));
          const newSubs = item.subCategories.filter(
            (sub) => !existingIds.has(sub._id)
          );
          return [...prev, ...newSubs];
        });
      }
    });
  };

  useEffect(() => {
    getCategories();
  }, []);

  const getData = async () => {
    const data = await fetch(`${API}/productList`);
    const json = await data.json();
    const prods = json.products || [];

    setProducts(prods);
    setSpin(false);

    const nextOriginal = {};

    const unassignedByCategory = {};
    prods
      .filter((p) => !p.subCategoryId)
      .forEach((p) => {
        if (!unassignedByCategory[p.categoryId]) {
          unassignedByCategory[p.categoryId] = [];
        }
        unassignedByCategory[p.categoryId].push(p);
      });

    Object.entries(unassignedByCategory).forEach(([categoryId, list]) => {
      const sorted = list.sort((a, b) => a.sortId - b.sortId);
      const key = `unassigned-${categoryId}`;
      nextOriginal[key] = sorted.map((p) => p._id);
    });

    const bySubcategory = {};
    prods
      .filter((p) => p.subCategoryId)
      .forEach((p) => {
        if (!bySubcategory[p.subCategoryId]) {
          bySubcategory[p.subCategoryId] = [];
        }
        bySubcategory[p.subCategoryId].push(p);
      });

    Object.entries(bySubcategory).forEach(([subId, list]) => {
      const sorted = list.sort((a, b) => a.sortId - b.sortId);
      const key = `sub-${subId}`;
      nextOriginal[key] = sorted.map((p) => p._id);
    });

    setOriginalOrders(nextOriginal);
    setChangedGroups({});
  };

  const updateProductOrder = async (order) => {
    await axios.put(`${API}/Admin/addProduct/order`, {
      order,
    });
    await getData();
  };

  const updateProductsSubcategory = async (subcategory) => {
    const subcategoryId =
      typeof subcategory === "string" ? subcategory : subcategory._id;

    const data = await axios.put(
      `${API}/Admin/addProduct/sub-category/${subcategoryId}`,
      {
        productIds: selectedProductIds,
      }
    );
    getData();
    setSelectedProductIds([]);
    return data;
  };

  const updateProductCategory = async (categoryId) => {
    const data = await axios.put(
      `${API}/Admin/addProduct/category/${categoryId}`,
      {
        productIds: selectedProductIds,
      }
    );
    getData();
    setSelectedProductIds([]);
    return data;
  };

  useEffect(() => {
    getData();
  }, []);

  const handleUnassignedDragEnd =
    (categoryId) =>
    ({ active, over }) => {
      if (!over || active.id === over.id) return;

      const groupKey = `unassigned-${categoryId}`;

      setProducts((prev) => {
        const unassigned = prev
          .filter((p) => p.categoryId === categoryId && !p.subCategoryId)
          .sort((a, b) => a.sortId - b.sortId);

        const other = prev.filter(
          (p) => !(p.categoryId === categoryId && !p.subCategoryId)
        );

        const oldIndex = unassigned.findIndex((p) => p._id === active.id);
        const newIndex = unassigned.findIndex((p) => p._id === over.id);
        if (oldIndex === -1 || newIndex === -1) return prev;

        const originalSortIds = unassigned
          .map((p) => p.sortId)
          .sort((a, b) => a - b);

        const newArray = arrayMove(unassigned, oldIndex, newIndex);

        newArray.forEach((item, idx) => {
          item.sortId = originalSortIds[idx];
        });

        const newOrderIds = newArray.map((p) => p._id);
        setChangedGroups((prevChanged) => ({
          ...prevChanged,
          [groupKey]:
            !!originalOrders[groupKey] &&
            JSON.stringify(originalOrders[groupKey]) !==
              JSON.stringify(newOrderIds),
        }));

        return [...other, ...newArray];
      });
    };

  const handleSubcategoryDragEnd =
    (subcategoryId) =>
    ({ active, over }) => {
      if (!over || active.id === over.id) return;

      const groupKey = `sub-${subcategoryId}`;

      setProducts((prev) => {
        const subProducts = prev
          .filter((p) => p.subCategoryId === subcategoryId)
          .sort((a, b) => a.sortId - b.sortId);

        const other = prev.filter((p) => p.subCategoryId !== subcategoryId);

        const oldIndex = subProducts.findIndex((p) => p._id === active.id);
        const newIndex = subProducts.findIndex((p) => p._id === over.id);
        if (oldIndex === -1 || newIndex === -1) return prev;

        const originalSortIds = subProducts
          .map((p) => p.sortId)
          .sort((a, b) => a - b);

        const newArray = arrayMove(subProducts, oldIndex, newIndex);

        newArray.forEach((item, idx) => {
          item.sortId = originalSortIds[idx];
        });

        const newOrderIds = newArray.map((p) => p._id);
        setChangedGroups((prevChanged) => ({
          ...prevChanged,
          [groupKey]:
            !!originalOrders[groupKey] &&
            JSON.stringify(originalOrders[groupKey]) !==
              JSON.stringify(newOrderIds),
        }));

        return [...other, ...newArray];
      });
    };

  const DownloadSheet = async () => {
    const data = await fetch(`${API}/`);
  };

  return (
    <>
      {updatingAvailability && <Spin />}
      {spin ? (
        <Spin />
      ) : (
        <div className={styles.entirepage}>
          <div className={styles.upper}>
            <Link to="/">
              <span className={styles.swag}>
                Website
                <span className={styles.greater}> &gt; </span>
              </span>
            </Link>
            <span className={styles.req}>Products</span>
          </div>
          <div className={styles.headingContainer}>
            <div className={styles.heading}>Products</div>
            <div className={styles.toggleContainer}>
              <span
                className={
                  !showingActiveProducts
                    ? styles.toggleLabelActive
                    : styles.toggleLabelInactive
                }
              >
                Draft
              </span>
              <Toggle
                status={showingActiveProducts}
                setStatus={setShowingActiveProducts}
              />
              <span
                className={
                  showingActiveProducts
                    ? styles.toggleLabelActive
                    : styles.toggleLabelInactive
                }
              >
                Active
              </span>
            </div>
          </div>
          <div className={styles.addingbutton}>
            <Link to="/products/add">
              <div className={styles.button}>
                <FaPlus className={styles.addicon} />
                Add Product
              </div>
            </Link>
            <div className={styles.button}>
              <FaUpload className={styles.addicon} />
              Upload via Sheet
            </div>
            <div className={styles.button} onClick={DownloadSheet}>
              <FaDownload className={styles.addicon} />
              Download Sheet
            </div>
            <div
              className={styles.button}
              onClick={() => {
                if (selectedProductIds.length === 0) return;
                setShowMoveDialog(true);
              }}
              style={{
                opacity: selectedProductIds.length === 0 ? 0.5 : 1,
                cursor:
                  selectedProductIds.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              Move to subcategory
            </div>
            <div
              className={styles.button}
              onClick={() => {
                if (selectedProductIds.length === 0) return;
                setShowMoveCategoryDialog(true);
              }}
              style={{
                opacity: selectedProductIds.length === 0 ? 0.5 : 1,
                cursor:
                  selectedProductIds.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              Move to category
            </div>
            <div
              className={styles.button}
              onClick={() => {
                if (selectedProductIds.length === 0) return;
                bulkUpdate(!showingActiveProducts);
              }}
              style={{
                opacity: selectedProductIds.length === 0 ? 0.5 : 1,
                cursor:
                  selectedProductIds.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              {"Set as " + (showingActiveProducts ? "Draft" : "Active")}
            </div>
          </div>
          <div className={styles.logosdisplay}>
            <table className={styles.displaytable}>
              <thead>
                <tr className={styles.productrowhead}>
                  <th>SN</th>
                  <th></th>
                  <th>Products</th>
                  <th>Brand</th>

                  <th>Price</th>
                  <th>Available</th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {categories.map((u, index) => {
                  const isExpanded = expandedCategories.includes(u._id);

                  const toggleCategory = () => {
                    setExpandedCategories((prev) =>
                      prev.includes(u._id)
                        ? prev.filter((id) => id !== u._id)
                        : [...prev, u._id]
                    );
                  };

                  const categoryProducts = products.filter(
                    (p) =>
                      p.categoryId === u._id &&
                      (showingActiveProducts ? p.available : !p.available)
                  );
                  const unassignedProducts = categoryProducts.filter(
                    (p) => !p.subCategoryId
                  );
                  const categorySubcategories = subcategories.filter(
                    (sub) => sub.categoryId === u._id
                  );
                  const unassignedGroupKey = `unassigned-${u._id}`;

                  return (
                    <React.Fragment key={u._id}>
                      <tr
                        style={{ cursor: "pointer" }}
                        onClick={toggleCategory}
                      >
                        <td className={styles.categoryname}>
                          <span
                            style={{
                              display: "inline-block",
                              marginRight: "8px",
                              transform: isExpanded
                                ? "rotate(90deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.15s ease-out",
                              fontSize: "14px",
                            }}
                          >
                            ▶
                          </span>
                          {u.name}
                        </td>
                        <td className={styles.categoryname}></td>
                        <td className={styles.categoryname}></td>
                        <td className={styles.categoryname}></td>
                        <td className={styles.categoryname}></td>
                        <td className={styles.categoryname}></td>
                        <td className={styles.categoryname}></td>
                        <td className={styles.categoryname}></td>
                      </tr>
                      {isExpanded && (
                        <>
                          {unassignedProducts.length > 0 && (
                            <>
                              <tr>
                                <td colSpan={8} style={{ textAlign: "right" }}>
                                  <button
                                    type="button"
                                    disabled={
                                      !changedGroups[unassignedGroupKey]
                                    }
                                    onClick={async () => {
                                      const ordered = products
                                        .filter(
                                          (p) =>
                                            p.categoryId === u._id &&
                                            !p.subCategoryId
                                        )
                                        .sort((a, b) => a.sortId - b.sortId);
                                      const payload = ordered.map((item) => ({
                                        productId: item._id,
                                        order: item.sortId,
                                      }));
                                      await updateProductOrder(payload);
                                    }}
                                    style={{
                                      padding: "6px 12px",
                                      borderRadius: "6px",
                                      border: "1px solid #191818",
                                      backgroundColor: changedGroups[
                                        unassignedGroupKey
                                      ]
                                        ? "#0F143A"
                                        : "#ccc",
                                      color: "#fff",
                                      fontSize: "12px",
                                      cursor: changedGroups[unassignedGroupKey]
                                        ? "pointer"
                                        : "not-allowed",
                                    }}
                                  >
                                    Save Order
                                  </button>
                                </td>
                              </tr>

                              <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleUnassignedDragEnd(u._id)}
                              >
                                <SortableContext
                                  items={unassignedProducts
                                    .slice()
                                    .sort((a, b) => a.sortId - b.sortId)
                                    .map((p) => p._id)}
                                  strategy={verticalListSortingStrategy}
                                >
                                  {unassignedProducts
                                    .slice()
                                    .sort((a, b) => a.sortId - b.sortId)
                                    .map((c) => (
                                      <SortableProductRow
                                        key={c._id}
                                        product={c}
                                        selectedProductIds={selectedProductIds}
                                        setSelectedProductIds={
                                          setSelectedProductIds
                                        }
                                        updateProductPrice={updateProductPrice}
                                        requestAvailabilityChange={
                                          requestAvailabilityChange
                                        }
                                      />
                                    ))}
                                </SortableContext>
                              </DndContext>
                            </>
                          )}

                          {categorySubcategories.map((sub) => (
                            <React.Fragment key={sub._id}>
                              <tr className={styles.productrow}>
                                <td
                                  className={styles.categoryname}
                                  style={{
                                    textDecoration: "underline",
                                    fontStyle: "italic",
                                  }}
                                >
                                  {sub.name}
                                </td>
                                <td className={styles.categoryname}></td>
                                <td className={styles.categoryname}></td>
                                <td className={styles.categoryname}></td>
                                <td className={styles.categoryname}></td>
                                <td className={styles.categoryname}></td>
                                <td className={styles.categoryname}></td>
                                <td className={styles.categoryname}>
                                  {categoryProducts.filter(
                                    (p) => p.subCategoryId === sub._id
                                  ).length > 0 && (
                                    <button
                                      type="button"
                                      disabled={
                                        !changedGroups[`sub-${sub._id}`]
                                      }
                                      onClick={async () => {
                                        const ordered = products
                                          .filter(
                                            (p) => p.subCategoryId === sub._id
                                          )
                                          .sort((a, b) => a.sortId - b.sortId);
                                        const payload = ordered.map((item) => ({
                                          productId: item._id,
                                          order: item.sortId,
                                        }));
                                        await updateProductOrder(payload);
                                      }}
                                      style={{
                                        padding: "6px 12px",
                                        borderRadius: "6px",
                                        border: "1px solid #191818",
                                        backgroundColor: changedGroups[
                                          `sub-${sub._id}`
                                        ]
                                          ? "#0F143A"
                                          : "#ccc",
                                        color: "#fff",
                                        fontSize: "12px",
                                        cursor: changedGroups[`sub-${sub._id}`]
                                          ? "pointer"
                                          : "not-allowed",
                                      }}
                                    >
                                      Save Order
                                    </button>
                                  )}
                                </td>
                              </tr>
                              {categoryProducts.filter(
                                (p) => p.subCategoryId === sub._id
                              ).length > 0 && (
                                <>
                                  <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleSubcategoryDragEnd(
                                      sub._id
                                    )}
                                  >
                                    <SortableContext
                                      items={categoryProducts
                                        .filter(
                                          (p) => p.subCategoryId === sub._id
                                        )
                                        .sort((a, b) => a.sortId - b.sortId)
                                        .map((p) => p._id)}
                                      strategy={verticalListSortingStrategy}
                                    >
                                      {categoryProducts
                                        .filter(
                                          (p) => p.subCategoryId === sub._id
                                        )
                                        .sort((a, b) => a.sortId - b.sortId)
                                        .map((c) => (
                                          <SortableProductRow
                                            key={c._id}
                                            product={c}
                                            selectedProductIds={
                                              selectedProductIds
                                            }
                                            setSelectedProductIds={
                                              setSelectedProductIds
                                            }
                                            updateProductPrice={
                                              updateProductPrice
                                            }
                                            requestAvailabilityChange={
                                              requestAvailabilityChange
                                            }
                                          />
                                        ))}
                                    </SortableContext>
                                  </DndContext>
                                </>
                              )}
                            </React.Fragment>
                          ))}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {showMoveDialog && (
            <div className={styles.moveDialogBackdrop}>
              <div className={styles.moveDialog}>
                <div className={styles.moveDialogHeader}>
                  <div className={styles.moveDialogTitle}>
                    Move products to subcategory
                  </div>
                  <button
                    type="button"
                    className={styles.moveDialogClose}
                    onClick={() => {
                      setShowMoveDialog(false);
                      setSelectedSubcategoryId("");
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div className={styles.moveDialogContent}>
                  {subcategories.length === 0 ? (
                    <div className={styles.moveDialogEmpty}>
                      No subcategories available.
                    </div>
                  ) : (
                    <div className={styles.moveDialogList}>
                      {subcategories.map((sub) => (
                        <label key={sub._id} className={styles.moveDialogItem}>
                          <input
                            type="radio"
                            name="subcategory"
                            value={sub._id}
                            checked={selectedSubcategoryId === sub._id}
                            onChange={() => setSelectedSubcategoryId(sub._id)}
                          />
                          <span className={styles.moveDialogItemInfo}>
                            <span className={styles.moveDialogItemName}>
                              {sub.name}
                            </span>
                            <span className={styles.moveDialogItemMeta}>
                              Sort: {sub.sortId}{" "}
                              {sub.isActive ? "(Active)" : "(Inactive)"}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className={styles.moveDialogActions}>
                  <button
                    type="button"
                    className={styles.moveDialogButtonSecondary}
                    onClick={() => {
                      setShowMoveDialog(false);
                      setSelectedSubcategoryId("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={styles.moveDialogButtonPrimary}
                    disabled={!selectedSubcategoryId}
                    onClick={async () => {
                      if (!selectedSubcategoryId) return;
                      const selectedSubcategory = subcategories.find(
                        (s) => s._id === selectedSubcategoryId
                      );
                      try {
                        await updateProductsSubcategory(selectedSubcategory);
                        setShowMoveDialog(false);
                        setSelectedSubcategoryId("");
                      } catch (e) {
                        console.error(
                          "Failed to move products to subcategory",
                          e
                        );
                      }
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          {showMoveCategoryDialog && (
            <div className={styles.moveDialogBackdrop}>
              <div className={styles.moveDialog}>
                <div className={styles.moveDialogHeader}>
                  <div className={styles.moveDialogTitle}>
                    Move products to category
                  </div>
                  <button
                    type="button"
                    className={styles.moveDialogClose}
                    onClick={() => {
                      setShowMoveCategoryDialog(false);
                      setSelectedCategoryId("");
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div className={styles.moveDialogContent}>
                  {categories.length === 0 ? (
                    <div className={styles.moveDialogEmpty}>
                      No categories available.
                    </div>
                  ) : (
                    <div className={styles.moveDialogList}>
                      {categories.map((category) => (
                        <label
                          key={category._id}
                          className={styles.moveDialogItem}
                        >
                          <input
                            type="radio"
                            name="category"
                            value={category._id}
                            checked={selectedCategoryId === category._id}
                            onChange={() => setSelectedCategoryId(category._id)}
                          />
                          <span className={styles.moveDialogItemInfo}>
                            <span className={styles.moveDialogItemName}>
                              {category.name}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className={styles.moveDialogActions}>
                  <button
                    type="button"
                    className={styles.moveDialogButtonSecondary}
                    onClick={() => {
                      setShowMoveCategoryDialog(false);
                      setSelectedCategoryId("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={styles.moveDialogButtonPrimary}
                    disabled={!selectedCategoryId}
                    onClick={async () => {
                      if (!selectedCategoryId) return;
                      try {
                        await updateProductCategory(selectedCategoryId);
                        setShowMoveCategoryDialog(false);
                        setSelectedCategoryId("");
                      } catch (e) {
                        console.error("Failed to move products to category", e);
                      }
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
          {pendingAvailability && (
            <div className={styles.moveDialogBackdrop}>
              <div className={styles.moveDialog}>
                <div className={styles.moveDialogHeader}>
                  <div className={styles.moveDialogTitle}>
                    {pendingAvailability.available
                      ? "Make product available?"
                      : "Make product unavailable?"}
                  </div>
                  <button
                    type="button"
                    className={styles.moveDialogClose}
                    onClick={() => setPendingAvailability(null)}
                  >
                    ✕
                  </button>
                </div>

                <div className={styles.moveDialogContent}>
                  <b>{pendingAvailability.product.name}</b> will be set as{" "}
                  {pendingAvailability.available ? "Active" : "Draft"}.
                </div>

                <div className={styles.moveDialogActions}>
                  <button
                    type="button"
                    className={styles.moveDialogButtonSecondary}
                    onClick={() => setPendingAvailability(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={styles.moveDialogButtonPrimary}
                    onClick={confirmAvailabilityChange}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default Product;

const SortableProductRow = ({
  product,
  selectedProductIds,
  setSelectedProductIds,
  updateProductPrice,
  requestAvailabilityChange,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: product._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isSelected = selectedProductIds.includes(product._id);
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [editedPrice, setEditedPrice] = useState("");

  const currentPrice = Math.round(
    product.price[0].cost * (1 + product.price[0].margin / 100)
  );

  const handleEditPriceClick = () => {
    setEditedPrice(currentPrice.toString());
    setIsEditingPrice(true);
  };

  const handleSavePrice = async () => {
    const editedValue = Number(editedPrice);

    if (isNaN(editedValue) || editedValue < 0) return;

    const price = product.price[0];

    const margin = Number(price.margin);

    const calculatedCost = Number((editedValue / (1 + margin)).toFixed(2));

    await updateProductPrice(product._id, calculatedCost);
    setIsEditingPrice(false);
  };

  const handleCancelEdit = () => {
    setIsEditingPrice(false);
    setEditedPrice("");
  };

  return (
    <tr ref={setNodeRef} style={style} className={styles.productrow}>
      <td>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => {
            const { checked } = e.target;
            setSelectedProductIds((prev) => {
              if (checked) {
                if (prev.includes(product._id)) return prev;
                return [...prev, product._id];
              }
              return prev.filter((id) => id !== product._id);
            });
          }}
          style={{
            marginRight: "8px",
          }}
        />
        {product.sortId}
      </td>
      <td>
        <img src={product.image[0]} alt="Logo" className={styles.logos}></img>
      </td>
      <td className={styles.productname}>{product.name}</td>
      <td>{product.brandName}</td>

      <td className={styles.desc}>
        {isEditingPrice ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span>₹</span>
              <input
                type="number"
                value={editedPrice}
                onChange={(e) => setEditedPrice(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSavePrice();
                  } else if (e.key === "Escape") {
                    handleCancelEdit();
                  }
                }}
                autoFocus
                style={{
                  width: "110px",
                  padding: "6px 10px",
                  border: "1px solid #191818",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
            </div>
            <div style={{ display: "flex", gap: "6px", marginTop: "2px" }}>
              <button
                onClick={handleSavePrice}
                style={{
                  padding: "6px 14px",
                  backgroundColor: "#0F143A",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                Save
              </button>
              <button
                onClick={handleCancelEdit}
                style={{
                  padding: "6px 14px",
                  backgroundColor: "#ccc",
                  color: "#000",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span>₹ {currentPrice}</span>
            <button
              type="button"
              onClick={handleEditPriceClick}
              title="Edit price"
              style={{
                padding: "4px 10px",
                borderRadius: "6px",
                border: "1px solid #191818",
                backgroundColor: "#ffffff",
                cursor: "pointer",
                fontSize: "12px",
                fontFamily: "Nunito, sans-serif",
                color: "#000",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "auto",
                minWidth: "auto",
              }}
            >
              Edit
            </button>
          </div>
        )}
      </td>
      <td>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Toggle
            status={!!product.available}
            setStatus={(value) =>
              requestAvailabilityChange(product, value)
            }
          />
        </div>
      </td>
      <td className={styles.edit}>
        <Link to={`/products/${product._id}`}>
          <div>Edit</div>
        </Link>
      </td>
      <td>
        <span
          {...listeners}
          {...attributes}
          style={{
            cursor: "grab",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FaArrowsAlt />
        </span>
      </td>
    </tr>
  );
};
